import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { VehiclePicker } from '@/components/common/VehiclePicker';
import { DriverPicker } from '@/components/common/DriverPicker';

const EVENT_TYPES = [
  'oil_change', 'oil_filter', 'air_filter', 'fuel_filter', 'cabin_filter',
  'brake_pads_front', 'brake_pads_rear', 'brake_discs', 'brake_fluid',
  'tire_replacement', 'tire_rotation', 'tire_puncture',
  'battery', 'alternator', 'starter', 'fuel_pump', 'water_pump',
  'coolant_flush', 'radiator', 'thermostat',
  'clutch', 'gearbox', 'transmission', 'differential',
  'suspension', 'shock_absorber', 'steering',
  'electrical_wiring', 'lights', 'ac_compressor', 'ac_recharge',
  'engine_overhaul', 'engine_repair', 'turbo',
  'bodywork', 'paint', 'glass',
  'periodic_inspection', 'general_service',
  'breakdown_recovery', 'accident_repair',
  'other',
];

interface VehicleLite { id: string; code: string; plateEn: string }
interface DriverLite { id: string; code: string; fullNameEn: string | null }
interface Advance {
  id: string; code: string;
  amount_sar: string;
  recovered_amount_sar: string | null;
  reconciled_expenses_sar?: string | null;
  effective_balance_sar?: string | null;
  status: string;
  given_at: string | null;
  advance_date: string;
}

export default function MaintenanceEventCreatePage() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<VehicleLite[]>([]);
  const [drivers, setDrivers] = useState<DriverLite[]>([]);
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  // PR-DROPDOWN-SCOPE MAY 12 — auto-link the payer driver to the picked
  // vehicle's current driver. Manual override sticks until the vehicle is
  // cleared.
  const [recommendedDriverId, setRecommendedDriverId] = useState<string | null>(null);
  const [autoFilledDriver, setAutoFilledDriver] = useState(false);
  const driverManualRef = useRef(false);
  const [eventType, setEventType] = useState('oil_change');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high' | 'urgent_breakdown'>('normal');
  const [driverComplaint, setDriverComplaint] = useState('');
  const [description, setDescription] = useState('');
  const [odometerAtService, setOdometerAtService] = useState('');
  // PR-S2 MAY 11 — paid from driver advance toggle + selection
  const [paidFromAdvance, setPaidFromAdvance] = useState(false);
  const [payerDriverId, setPayerDriverId] = useState('');
  const [advances, setAdvances] = useState<Advance[]>([]);
  const [advanceId, setAdvanceId] = useState('');
  const [expenseAmountSar, setExpenseAmountSar] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient.get<{ items?: VehicleLite[] }>('/api/v1/vehicles?status=active&pageSize=200')
      .then(r => setVehicles(r.data.items ?? []))
      .catch(() => setVehicles([]));
    apiClient.get<{ items?: DriverLite[] }>('/api/v1/drivers?status=active&pageSize=200')
      .then(r => setDrivers(r.data.items ?? []))
      .catch(() => setDrivers([]));
  }, []);

  // PR-S2: when driver changes (or toggle flips on), load active advances
  useEffect(() => {
    if (!paidFromAdvance || !payerDriverId) { setAdvances([]); setAdvanceId(''); return; }
    apiClient.get<Advance[]>(`/api/v1/driver-advances-v2?driverId=${payerDriverId}&status=paid`)
      .then((r) => {
        const arr = Array.isArray(r.data) ? r.data : [];
        // Filter to those with remaining balance.
        const open = arr.filter(a => Number(a.effective_balance_sar ?? a.amount_sar ?? 0) > 0);
        setAdvances(open);
      })
      .catch(() => setAdvances([]));
  }, [paidFromAdvance, payerDriverId]);

  const selectedAdvance = advances.find(a => a.id === advanceId);
  const advanceBalance = selectedAdvance ? Number(selectedAdvance.effective_balance_sar ?? selectedAdvance.amount_sar ?? 0) : 0;
  const expenseAmount = Number(expenseAmountSar || 0);
  const overBalance = paidFromAdvance && !!selectedAdvance && expenseAmount > advanceBalance;

  async function submit() {
    if (!vehicleId) { setError('Pick a vehicle'); return; }
    if (paidFromAdvance) {
      if (!payerDriverId) { setError('Pick the driver who used the advance'); return; }
      if (!advanceId)     { setError('Pick the advance the driver used'); return; }
      if (!expenseAmount) { setError('Enter the expense amount paid from advance'); return; }
      if (overBalance)    { setError(`This expense (${expenseAmount} SAR) exceeds the advance's remaining balance (${advanceBalance} SAR).`); return; }
    }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        vehicleId, eventType, priority,
        driverComplaint: driverComplaint || undefined,
        description: description || undefined,
        odometerAtService: odometerAtService ? Number(odometerAtService) : undefined,
      };
      if (paidFromAdvance) {
        payload.paidFromAdvanceId = advanceId;
        payload.payerDriverId     = payerDriverId;
        payload.expenseAmountSar  = expenseAmount;
      }
      const r = await apiClient.post<{ id: string; code: string }>('/api/v1/maintenance/events', payload);
      navigate(`/dashboard/maintenance/${r.data.id}`);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally { setSubmitting(false); }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/maintenance"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Maintenance</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wrench className="h-4 w-4" /> New event</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        {error && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>}

        <fieldset className="space-y-3 rounded-lg border border-border p-4">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground">Vehicle & type</legend>
          <div className="space-y-1">
            <Label>Vehicle *</Label>
            <VehiclePicker
              value={vehicleId}
              onChange={(id, v) => {
                setVehicleId(id);
                const cd = v?.currentDriver?.id ?? null;
                setRecommendedDriverId(cd);
                if (!id) { driverManualRef.current = false; setAutoFilledDriver(false); return; }
                if (!driverManualRef.current && cd && paidFromAdvance && cd !== payerDriverId) {
                  setPayerDriverId(cd);
                  setAutoFilledDriver(true);
                }
              }}
              placeholder="Select vehicle…"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Event type *</Label>
              <select value={eventType} onChange={(e) => setEventType(e.target.value)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Priority *</Label>
              <select value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent_breakdown">Urgent / Breakdown</option>
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset className="space-y-3 rounded-lg border border-border p-4">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground">Reported issue</legend>
          <div className="space-y-1">
            <Label>Driver complaint (bilingual EN/AR)</Label>
            <textarea value={driverComplaint} onChange={(e) => setDriverComplaint(e.target.value)} rows={3} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base"
              placeholder="Engine warning light came on after fuel stop in Riyadh. Sound from rear axle…" />
          </div>
          <div className="space-y-1">
            <Label>Internal description</Label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
          </div>
          <div className="space-y-1">
            <Label>Odometer at service (km)</Label>
            <Input type="number" min={0} value={odometerAtService} onChange={(e) => setOdometerAtService(e.target.value)} />
          </div>
        </fieldset>

        {/* PR-S2 MAY 11 — paid from driver advance toggle */}
        <fieldset className="space-y-3 rounded-lg border border-border p-4">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground">Reconcile with driver advance</legend>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={paidFromAdvance} onChange={(e) => setPaidFromAdvance(e.target.checked)} className="h-4 w-4" />
            Paid from driver advance?
          </label>
          {paidFromAdvance && (
            <div className="space-y-3 pl-6 border-l-2 border-amber-500/30">
              <div className="space-y-1">
                <Label>
                  Driver who paid *
                  {autoFilledDriver && (
                    <span className="ms-2 text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                      auto · click to change
                    </span>
                  )}
                </Label>
                <DriverPicker
                  value={payerDriverId || null}
                  onChange={(id) => {
                    setPayerDriverId(id ?? '');
                    driverManualRef.current = id !== null;
                    if (id !== recommendedDriverId) setAutoFilledDriver(false);
                  }}
                  preferredDriverId={recommendedDriverId}
                  placeholder="Select driver…"
                />
              </div>
              <div className="space-y-1">
                <Label>Active advance *</Label>
                <select value={advanceId} onChange={(e) => setAdvanceId(e.target.value)} disabled={!payerDriverId}
                  className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base disabled:opacity-50">
                  <option value="">{payerDriverId ? (advances.length === 0 ? 'No paid advances with remaining balance' : 'Select advance…') : 'Pick driver first'}</option>
                  {advances.map((a) => {
                    const balance = Number(a.effective_balance_sar ?? a.amount_sar ?? 0);
                    const given = a.given_at ? new Date(a.given_at).toLocaleDateString('en-GB') : a.advance_date;
                    return (
                      <option key={a.id} value={a.id}>
                        {a.code} · {Number(a.amount_sar).toFixed(0)} SAR given {given} · {balance.toFixed(0)} SAR remaining
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Expense amount paid from advance (SAR) *</Label>
                <Input type="number" step="0.01" min={0} value={expenseAmountSar} onChange={(e) => setExpenseAmountSar(e.target.value)} />
                {selectedAdvance && (
                  <p className={'text-[11px] ' + (overBalance ? 'text-rose-700 dark:text-rose-400 font-medium' : 'text-muted-foreground')}>
                    {overBalance
                      ? `This expense (${expenseAmount} SAR) exceeds the advance's remaining balance (${advanceBalance.toFixed(2)} SAR). Lower the amount, or save without reconciliation and split across multiple advances later.`
                      : `Advance has ${advanceBalance.toFixed(2)} SAR remaining. Reconciling reduces that to ${(advanceBalance - expenseAmount).toFixed(2)} SAR.`}
                  </p>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground italic">
                This reduces the advance balance by the expense amount. After save, the driver owes (amount - expense - recovered).
              </p>
            </div>
          )}
        </fieldset>

        <div className="flex justify-end gap-2">
          <Link to="/dashboard/maintenance"><Button variant="outline">Cancel</Button></Link>
          <Button onClick={submit} disabled={submitting || !vehicleId}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Create event (status = reported)
          </Button>
        </div>
      </main>
    </div>
  );
}
